/**
 * Utilities that operate on the iframe document — injecting editing styles/scripts
 * and measuring document content height for dynamic iframe sizing.
 */

/**
 * Injects screen-only editing styles and a bootstrap script into the report HTML
 * so it can be edited inside a sandboxed iframe via `document.designMode = 'on'`.
 */
// PDF margins (from pdfService.js's Playwright page.pdf() call, format: 'A4'):
//   Portrait:  top 19.05mm ≈ 72px, right 25.4mm ≈ 96px, bottom 25.4mm ≈ 96px, left 25.4mm ≈ 96px
//   Landscape: top 19.05mm ≈ 72px, right 25.4mm ≈ 96px, bottom 19.05mm ≈ 72px, left 25.4mm ≈ 96px
// True A4 at 96dpi is 793.7×1122.5px, but the editor renders pages at 1100px wide, 1600px tall
// on screen (bumped up from the original 990×1496 box — see git history for that derivation).
// Padding was intentionally left unscaled at this larger size (still 85/115/50/115), so it is
// no longer proportional to true A4 the way it was at 990px wide; the landscape page size below
// and the .photos/.--active outline overrides are scaled to the new 1100px-wide box to stay
// consistent with each other, using the ratio to the original 990px baseline (1100/990 ≈ 1.1111).
// 1600's content cutoff with this padding (1600 - 85 - 50 = 1465px) is still above the ~1361px
// minimum a known section ("2.6 Stored Materials" in a magic report) needs to keep its heading +
// first paragraph together (see findSplitPoint / break-inside:avoid handling in dom.ts).
export function injectEditing(html: string): string {
  const inject = `<style data-editor-inject="true">
/* ── Screen-only editor layout (stripped before PDF render) ── */
@media screen {
  html, body {
    background: #f0f0f0 !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    overflow: visible !important;
  }

  /* Pages fill the full iframe width so the editor feels like FitH in the PDF viewer.
     Padding is scaled by the width factor 990/793.7 ≈ 1.2473 (NOT the height factor) so
     printable margins stay proportional to true A4 — see the comment above. min-height is
     taller than that same proportional scaling would give (~1400px), as extra slack to
     correct content-per-page overflow math. Bottom padding is reduced to 45px (below both
     the proportional value 119.7px and top padding 89.8px) to reclaim more of that slack
     as usable content height. */
  body.editor-active .page {
    position: relative;
    background: white !important;
    width: 1100px !important;
    min-height: 1600px !important;
    margin: 8px auto !important;
    padding: 85px 115px 50px 115px !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  body.editor-active .page.magic-cover-page {
    padding: 64px 84px 64px 84px !important;
  }
  body.editor-active .page.magic-cover-page::after {
    content: '';
    position: absolute;
    top: 32px;
    right: 42px;
    bottom: 32px;
    left: 42px;
    border: 1px solid rgb(0, 0, 0);
    border-radius: 10px;
    pointer-events: none;
    z-index: 0;
  }
  /* Photos grid is 2 × 425px images + 10px gap = 860px, wider than the standard content
     box (1100 - 115*2 = 870px). That's already just enough room at the current page width,
     but the narrower side padding is kept as extra breathing room so the grid doesn't sit
     flush against the standard content-box edge (990 - 60*2 = 870px was the original, tighter
     derivation before the page width was bumped to 1100px). */
  body.editor-active .page.photos {
    padding-left: 60px !important;
    padding-right: 60px !important;
  }
  /* Landscape pages: dimensions and padding derived independently from true A4-landscape
     size (1122.52×793.70px at 96dpi) scaled by the same width factor as portrait
     (1100/793.7 ≈ 1.386), NOT copied from portrait's own (differently-tuned) numbers —
     landscape's real margins are top/right/bottom/left 72/96/72/96px (bottom equals top,
     unlike portrait where bottom is 96px), so portrait's reduced bottom-padding value must
     not be reused here; doing so previously made the editor's landscape content area
     taller than the true PDF, letting extra rows of the continuation-sheet table fit that
     the PDF's page wouldn't. Width/height here are scaled from the original 1400.1×990 box
     by the same 1100/990 ratio the portrait page width grew by, to stay proportionally
     consistent with portrait rather than independently re-deriving from the mm spec. */
  body.editor-active .page.landscape {
    width: 1555.67px !important;
    height: 1100px !important;
    min-height: unset !important;
    overflow: hidden !important;
    padding: 64px 84px 64px 84px !important;
  }

  .page *:not(pre):not(code) {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  /* Prevent headings from being stranded at the bottom of a page without following content */
  body.editor-active .page h1,
  body.editor-active .page h2,
  body.editor-active .page h3,
  body.editor-active .page h4,
  body.editor-active .page h5,
  body.editor-active .page h6,
  body.editor-active .page .major-section-header,
  body.editor-active .page .section-header,
  body.editor-active .page .budget-title,
  body.editor-active .page .continuation-title,
  body.editor-active .page .photos-title {
    break-after: avoid;
    page-break-after: avoid;
  }

  /* Footer replica — pinned to bottom margin area (Playwright renders it in the 96px bottom margin) */
  .__pg-footer__ {
    position: absolute;
    bottom: 30px;
    left: 96px;
    right: 96px;
    width: auto;
    text-align: center;
    font-family: 'Times New Roman', serif;
    font-size: 8px;
    color: #555;
    -webkit-user-modify: read-only;
    user-select: none;
    pointer-events: none;
  }
  .__pg-footer__ .footer-name { font-weight: bold; letter-spacing: 0.05em; }

  .page {
    box-shadow: 0 1px 4px rgba(0,0,0,0.15) !important;
  }
  .page.--active::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 1096px;
    height: 1596px;
    border: 2px dashed #077085;
    pointer-events: none;
    z-index: 0;
  }
  .page.landscape.--active::before {
    width: 1551.67px;
    height: 1096px;
  }

  .__pg-sep__ {
    min-height: 34px;
    box-sizing: border-box;
    margin-left: 0;
    margin-right: 0;
    background: #dadce0;
    border-top: 1px solid #bfc1c4;
    border-bottom: 1px solid #bfc1c4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 6px 10px;
    position: relative;
    -webkit-user-modify: read-only;
    user-select: none;
    pointer-events: auto;
  }
  .__pg-sep__ .__pg-sep__label {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    border: 1px solid #c4c7c5;
    border-radius: 10px;
    padding: 2px 14px;
    font-family: Arial, sans-serif;
    font-size: 10px;
    color: #5f6368;
    letter-spacing: 0.04em;
    pointer-events: none;
    white-space: nowrap;
    z-index: 0;
  }
  .__pg-sep__ [data-pg-sep-action="insert-below"],
  .__pg-sep__ [data-pg-sep-action="delete-page"] {
    position: relative;
    z-index: 1;
  }
  .__pg-sep__ .__pg-sep__btn {
    font-family: Arial, sans-serif;
    font-size: 10px;
    color: #5f6368;
    padding: 3px 10px;
    border-radius: 6px;
    border: 1px solid #c4c7c5;
    background: #fff;
    line-height: 1.2;
    pointer-events: auto;
    cursor: pointer !important;
    -webkit-user-modify: read-only;
  }
  .__pg-sep__ .__pg-sep__btn:hover { background: #f1f3f4; }
  .__pg-sep__ .__pg-sep__btn--danger { color: #c5221f; border-color: #e8a29f; }
  .__pg-sep__ .__pg-sep__btn--danger:hover { background: #fce8e6; }

  .__pg-badge__ {
    position: absolute;
    top: 6px;
    right: 10px;
    font-family: Arial, sans-serif;
    font-size: 9px;
    color: #b0b3b8;
    background: rgba(255,255,255,0.85);
    border: 1px solid #e4e6ea;
    border-radius: 4px;
    padding: 1px 6px;
    -webkit-user-modify: read-only;
    user-select: none;
    pointer-events: none;
  }

  ::selection { background: rgba(26,115,232,0.18); }
  * { cursor: text !important; }
  a, button { cursor: text !important; }
  .__pg-sep__, .__pg-badge__ { cursor: default !important; }
  .__pg-sep__ .__pg-sep__btn { cursor: pointer !important; }
}

@media print {
  .__pg-sep__, .__pg-badge__ { display: none !important; }
}
</style>
<script data-editor-inject="true">
document.addEventListener('DOMContentLoaded', function () {
  document.body.classList.add('editor-active');
  document.documentElement.style.overflow = 'visible';
  document.body.style.overflow = 'visible';
  document.documentElement.style.minHeight = '0';
  document.body.style.minHeight = '0';
  document.documentElement.style.height = 'auto';
  document.body.style.height = 'auto';

  var pages = Array.from(document.querySelectorAll('.page'));
  var total = pages.length;

  pages.forEach(function (page, i) {
    var badge = document.createElement('div');
    badge.className = '__pg-badge__';
    badge.textContent = 'Page ' + (i + 1) + ' / ' + total;
    page.appendChild(badge);

    // Add footer replica to portrait pages (not landscape, which has no footer in PDF)
    if (!page.classList.contains('landscape')) {
      var footer = document.createElement('div');
      footer.className = '__pg-footer__';
      footer.innerHTML = '<div class="footer-name">CD CONSTRUCTION CONSULTING</div>' +
        '<div>13215 BEE CAVE PARKWAY, BLDG A, SUITE 130, AUSTIN, TEXAS 78738  512.476.3537 CDCC.COM</div>';
      page.appendChild(footer);
    }

    if (i < total - 1) {
      var sep = document.createElement('div');
      sep.className = '__pg-sep__';
      sep.innerHTML = '<span>&#8212; Page ' + (i + 1) + ' / ' + total + ' &#8212;</span>';
      page.after(sep);
    }
  });

  document.designMode = 'on';

  document.addEventListener('click', function (e) {
    var a = e.target.closest('a');
    if (a) e.preventDefault();
  }, true);

  function updateActivePage() {
    var sel = window.getSelection();
    var node = sel && sel.rangeCount ? sel.getRangeAt(0).startContainer : null;
    var el = node ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : node) : null;
    var activePage = null;
    while (el && el !== document.documentElement) {
      if (el.classList && el.classList.contains('page')) { activePage = el; break; }
      el = el.parentElement;
    }
    document.querySelectorAll('.page').forEach(function (p) {
      if (p === activePage) p.classList.add('--active');
      else p.classList.remove('--active');
    });
  }

  document.addEventListener('selectionchange', updateActivePage);
  document.addEventListener('click', updateActivePage);

  var firstPage = document.querySelector('.page');
  if (firstPage) firstPage.classList.add('--active');
});
</script>`;

  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, inject + '\n</head>');
  if (/<body[\s>]/i.test(html)) return html.replace(/(<body[\s>])/i, inject + '\n$1');
  return inject + '\n' + html;
}

/**
 * Measures the rendered content height of the iframe document by summing the
 * bounding boxes of `.page` and `.__pg-sep__` elements, rather than reading
 * `body.scrollHeight` which can be inflated by `min-height: 100%` on the body.
 */
export function getIframeDocumentContentHeightPx(doc: Document): number {
  if (!doc?.body) return 0;
  const win = doc.defaultView;
  if (!win) return 0;

  const bodyTop = doc.body.getBoundingClientRect().top;
  let maxBottom = 0;

  doc.querySelectorAll('.page, .__pg-sep__').forEach(el => {
    const r = el.getBoundingClientRect();
    maxBottom = Math.max(maxBottom, r.bottom - bodyTop);
  });

  if (maxBottom > 1) return Math.ceil(maxBottom);

  return Math.max(doc.body.scrollHeight ?? 0, doc.documentElement?.scrollHeight ?? 0);
}

const SETTLE_LAYOUT_TIMEOUT_MS = 15000;

/**
 * Waits for web fonts and images inside the iframe document so multi-page
 * layout and `getIframeDocumentContentHeightPx` reflect the final heights.
 * Times out so a hung asset cannot block the editor forever.
 */
export async function settleIframeDocumentLayout(doc: Document): Promise<void> {
  const win = doc.defaultView;
  if (!win) return;

  let timeoutId: number | undefined;
  const timeoutPromise = new Promise<void>(resolve => {
    timeoutId = win.setTimeout(resolve, SETTLE_LAYOUT_TIMEOUT_MS) as number;
  });

  try {
    const fontsReady = doc.fonts?.ready ?? Promise.resolve();
    const incomplete = Array.from(doc.images).filter(img => !img.complete);
    await Promise.race([
      Promise.all([
        fontsReady,
        ...incomplete.map(
          img =>
            new Promise<void>(resolve => {
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            }),
        ),
      ]),
      timeoutPromise,
    ]);
  } finally {
    if (timeoutId != null) win.clearTimeout(timeoutId);
  }

  await new Promise<void>(resolve => {
    win.requestAnimationFrame(() => {
      win.requestAnimationFrame(() => resolve());
    });
  });
}
